import type React from "react"
import { ReactNode } from "react"
import { forwardRef } from "react"

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode
  error?: string
  className?: string
  fullWidth?: boolean
  icon?: React.ReactNode
  helperText?: string
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, className = "", fullWidth = false, icon, helperText, ...props }, ref) => {
    const inputClasses = `
      px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200
      ${error ? "border-red-500 focus:ring-red-500" : "border-gray-300"} 
      ${fullWidth ? "w-full" : ""} 
      ${props.disabled ? "bg-gray-100 cursor-not-allowed text-gray-500" : "bg-white"}
      ${icon ? "pl-10" : ""}
      ${className}
    `

    return (
      <div className={`${fullWidth ? "w-full" : ""} space-y-1.5`}>
        {label && (
          <label className="block text-sm font-medium text-gray-700">
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <div className="relative">
          {icon && <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">{icon}</div>}
          <input ref={ref} className={inputClasses} {...props} />
        </div>
        {error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : helperText ? (
          <p className="text-sm text-gray-500">{helperText}</p>
        ) : null}
      </div>
    )
  },
)

Input.displayName = "Input"

export default Input

